<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems, TransitionChild, TransitionRoot } from '@headlessui/vue'

import { z } from 'zod'

import { useLocalStorage } from '@vueuse/core'
import ArrowUturnLeftIcon from '@heroicons/vue/24/outline/esm/ArrowUturnLeftIcon'
import Bars3Icon from '@heroicons/vue/24/outline/esm/Bars3Icon'
import BellAlertIcon from '@heroicons/vue/24/outline/esm/BellAlertIcon'
import BellIcon from '@heroicons/vue/24/outline/esm/BellIcon'
import BookmarkIcon from '@heroicons/vue/24/outline/esm/BookmarkIcon'
import BookmarkSquareIcon from '@heroicons/vue/24/outline/esm/BookmarkSquareIcon'
import CogIcon from '@heroicons/vue/24/outline/esm/CogIcon'
import InboxIcon from '@heroicons/vue/24/outline/esm/InboxIcon'
import MicrophoneIcon from '@heroicons/vue/24/outline/esm/MicrophoneIcon'
import NewspaperIcon from '@heroicons/vue/24/outline/esm/NewspaperIcon'
import { format } from 'date-fns'
import VideoCameraIcon from '@heroicons/vue/24/outline/esm/VideoCameraIcon'
import XMarkIcon from '@heroicons/vue/24/outline/esm/XMarkIcon'
import GiftIcon from '@heroicons/vue/24/outline/esm/GiftIcon'

import WhatsappFloatingButton from '~/components/WhatsappFloatingButton.vue'

const whatsappFloatingButton = ref<typeof WhatsappFloatingButton>()

const navigationItems = computed(() => {
  return [
    { name: 'Community', href: '/' },
    // { name: 'Academy', href: '/academy' },
    { name: 'I tuoi Corsi', href: '/lessons' },
    { name: 'Masterclass', href: '/masterclass' },
    { name: 'Cleopatra', href: 'https://app.cleopatraplatform.com', external: true },
    // { name: 'Tutor', href: '/tutor' },
    // { name: 'Negozio', href: '/shop' },
    // { name: 'Mangusta', href: '/mangusta' },
  ]
})

const user = useAuthUser()
const auth = useAuth()

const canAccessAdminArea = computed(() => {
  const rolesWithAdminAreaAccess = [
    'ADMIN',
    'MODERATOR',
    'EDITOR',
    'SALES',
    'SUPPORT',
  ]

  return rolesWithAdminAreaAccess.includes(user.value?.role as string)
})

async function signOut() {
  try {
    await auth.signOut()
  }
  catch {
    //
  }
}

const { $trpc } = useNuxtApp()

// for whatsapp button
const { data: authData } = useAuth()
const { data: whatsappButtonData, refresh: refreshButtonData } = useLazyAsyncData(() => $trpc.user.getMyRepresentativeData.query())

const { data: notificationsData, pending: _loading, refresh } = useLazyAsyncData(() => $trpc.notifications.notifications.query(), {
  server: false,
})

watch(authData, () => {
  refreshButtonData()
})

interface Notification {
  id: string
  label: string
  type: string
  readAt: Date | null
  onClick?: () => Promise<void> | void
}

const notificationDataSchema = z
  .object({
    points: z.number().nullish(),
    postTitle: z.string().nullish(),
  })
  .nullish()

function parseNotificationData(data: unknown) {
  const parseResult = notificationDataSchema.safeParse(data)
  return parseResult.success ? parseResult.data : null
}

const notifications = computed(() => {
  const notifications = notificationsData.value || []

  return notifications.map((notification) => {
    let label = ''

    const points = parseNotificationData(notification.data)?.points

    switch (notification.type) {
      case 'community_post_upvote':
      {
        const postTitle = parseNotificationData(notification.data)?.postTitle || ''

        label = postTitle ? `Il tuo post ${postTitle} ha ricevuto un mi piace!` : `Il tuo post ha ricevuto un mi piace!`
        break
      }
      case 'community_post_comment_upvote':
        label = 'Il tuo commento ha ricevuto un mi piace!'
        break
      case 'purchased_basic_plan':
        label = points ? `Hai ottenuto ${points} punt${points > 1 ? 'i' : 'o'} per l'acquisto del piano Base` : 'Hai ottenuto dei punti per l\'acquisto del piano Base'
        break
      case 'purchased_gold_plan':
        label = points ? `Hai ottenuto ${points} punt${points > 1 ? 'i' : 'o'} per l'acquisto del piano Gold` : 'Hai ottenuto dei punti per l\'acquisto del piano Gold'
        break
      case 'purchased_platinum_plan':
        label = points ? `Hai ottenuto ${points} punt${points > 1 ? 'i' : 'o'} per l'acquisto del piano Platinum` : 'Hai ottenuto dei punti per l\'acquisto del piano Platinum'
        break
      case 'purchased_diamond_plan':
        label = points ? `Hai ottenuto ${points} punt${points > 1 ? 'i' : 'o'} per l'acquisto del piano Diamond` : 'Hai ottenuto dei punti per l\'acquisto del piano Diamond'
        break
      case 'purchased_black_plan':
        label = points ? `Hai ottenuto ${points} punt${points > 1 ? 'i' : 'o'} per l'acquisto del piano Black` : 'Hai ottenuto dei punti per l\'acquisto del piano Black'
        break
      case 'purchased_product':
        label = points ? `Hai ottenuto ${points} punt${points > 1 ? 'i' : 'o'} per l'acquisto di un prodotto` : 'Hai ottenuto dei punti per l\'acquisto di un prodotto'
        break
      case 'hours_watched':
        label = points ? `Hai ottenuto ${points} punt${points > 1 ? 'i' : 'o'} per le tue ore di ascolto` : 'Hai ottenuto dei punti per le tue ore di ascolto'
        break
      case 'course_reminder_notification':
        label = 'Oggi devi riguardare un corso, vai nella sezione dei reminder per vederlo!'
        break
      case 'lesson_reminder_notification':
        label = 'Oggi devi riguardare una lezione, vai nella sezione dei reminder per vederla!'
        break
      case 'tutor_chat_message':
        label = 'Hai ricevuto un messaggio dal tuo tutor'
        break
      default:
        label = 'Hai una notifica'
    }

    return {
      id: notification.id,
      label,
      type: notification.type,
      readAt: notification.readAt,
      createdAt: notification.createdAt,
    }
  })
})

async function performNotificationDefaultAction(notification: Notification) {
  if (notification.type === 'lesson_reminder_notification' || notification.type === 'course_reminder_notification') {
    await navigateTo('/reminders')
  }

  if (notification.type === 'tutor_chat_message') {
    await navigateTo('/tutor')
  }

  await $trpc.notifications.markNotificationAsRead.mutate({
    notificationId: notification.id,
  })

  notification.readAt = new Date()

  await refresh()
}

async function markNotificationAsRead(notification: Notification) {
  await $trpc.notifications.markNotificationAsRead.mutate({
    notificationId: notification.id,
  })

  notification.readAt = new Date()

  await refresh()
}

async function markAllNotificationsAsRead() {
  await $trpc.notifications.markAllNotificationAsRead.mutate()
  await refresh()
}

const popUpStorage = 'nov_25_offer_2024_4'
const localPopUpStorage = useLocalStorage('popup', '')
const EXPIRED = (new Date() > new Date('2024-11-30T23:59:00+01:00'))
const showCurrentPopup = computed(() => {
  return localPopUpStorage.value !== popUpStorage && !EXPIRED
})

function closeCurrentPopup() {
  // showCurrentPopup.value = false
  localPopUpStorage.value = popUpStorage
}

const cleopatraPopupStorageKey = 'cleopatra_plan_popup_v1'
const localCleopatraPopupStorage = useLocalStorage(cleopatraPopupStorageKey, '')
const showCleopatraPopup = computed(() => {
  return !!user.value?.cleopatraPlanName && localCleopatraPopupStorage.value !== 'dismissed'
})

function closeCleopatraPopup() {
  localCleopatraPopupStorage.value = 'dismissed'
}

const isBannerExpired = computed(() => {
  return new Date() > new Date('2024-06-18T23:59')
})

const isNavigationMenuOpen = ref(false)
const isUserMenuOpen = ref(false)
function openNavigationMenu() {
  isNavigationMenuOpen.value = true
  isUserMenuOpen.value = false
}
function openUserMenu() {
  isUserMenuOpen.value = true
  isNavigationMenuOpen.value = false
}
</script>

<template>
  <div class="flex flex-col flex-1 text-black bg-gray-100 dark:bg-neutral-900">
    <Disclosure
      v-slot="{ open }"
      as="nav"
      class="sticky top-0 z-20 w-full bg-white shadow-md dark:shadow-none dark:border-b dark:border-neutral-700/70 dark:bg-neutral-900 dark:text-white"
    >
      <div class="flex flex-col-reverse md:flex-col w-full">
        <!-- Banner Offer -->
        <template v-if="!isBannerExpired">
          <div v-if="!open" class="w-full flex z-20 items-center justify-center flex-1 bg-default-blue p-2 flex-wrap shadow-md text-white">
            <div class="flex justify-around flex-col md:flex-row items-center gap-2 md:gap-5 text-center">
              <div class="p-1 font-bold tracking-tight">
                È uscito ALPHA MINDSET! Corso segreto per forgiare un MINDSET da vero uomo di successo
              </div>
              <div class="bg-green-500 rounded-md p-1 font-bold tracking-tight">
                <a target="_blank" href="https://mindset-alpha.com/?fonte_wbe=popup">
                  Clicca qui e scopri di più
                </a>
              </div>
            </div>
          </div>
        </template>

        <div class="w-full px-4 flex md:mx-auto md:max-w-7xl sm:px-6 lg:px-8">
          <div class="flex flex-grow items-center justify-between lg:gap-2 md:gap-0">
            <!-- Logo -->
            <div class="hidden md:block relative self-start flex-shrink-0 w-[78px]">
              <NuxtLink
                to="/"
                class="flex items-center justify-center px-2 py-4 absolute top-0 inset-x-0 bg-default-blue rounded-b-[20%]"
              >
                <div class="bg-white rounded-full p-2 w-[40px] h-[40px]">
                  <img class="size-full" src="/logo-dark.svg">
                </div>
              </NuxtLink>
            </div>

            <div class="flex-nowrap hidden md:flex">
              <template v-for="n in navigationItems" :key="n.name">
                <a
                  v-if="n.external"
                  v-bind="$attrs"
                  :href="n.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="transition lg:min-w-[7vw] md:min-w-[4vw] inline-flex items-center justify-center lg:px-4 md:px-2 py-4 lg:text-sm md:text-xs font-medium text-gray-400 border-b-4 border-transparent hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {{ n.name }}
                </a>
                <NuxtLink
                  v-else
                  v-slot="{ href, navigate, isExactActive }"
                  custom
                  :href="n.href"
                >
                  <a
                    v-bind="$attrs" :href="href" :class="[isExactActive ? 'border-[#0095FF]' : 'border-transparent']"
                    class="transition lg:min-w-[7vw] md:min-w-[4vw] inline-flex items-center justify-center lg:px-4 md:px-2 py-4 lg:text-sm md:text-xs font-medium text-gray-400 border-b-4 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300"
                    @click="navigate"
                  >
                    {{ n.name }}
                  </a>
                </NuxtLink>
              </template>
            </div>

            <div class="flex-shrink-0 hidden lg:gap-3 md:gap-1 md:flex md:items-center">
              <!-- TODO: style for this button -->
              <ToggleDarkButton />

              <Menu as="div" class="relative">
                <div class="flex">
                  <MenuButton class="relative flex flex-shrink-0 mx-1 text-sm rounded-full focus:outline-none">
                    <span class="sr-only">View notifications</span>
                    <BellIcon class="w-6 h-6" aria-hidden="true" />
                    <div
                      v-if="notifications.length > 0"
                      class="absolute px-1 text-xs text-white rounded-full -right-1 -bottom-1 bg-default-blue"
                    >
                      {{ notifications.length }}
                    </div>
                  </MenuButton>
                </div>
                <transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100"
                  leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100"
                  leave-to-class="transform scale-95 opacity-0"
                >
                  <MenuItems
                    class="absolute max-h-[400px] overflow-y-auto custom-scrollbar right-0 z-10 py-1 mt-2 text-gray-700 origin-top-right bg-white shadow-lg rounded-xl w-96 dark:bg-neutral-800 dark:text-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <template v-if="notifications.length === 0">
                      <MenuItem v-slot="{ active }">
                        <button
                          type="button" class="block w-full px-4 py-2 text-sm"
                          :class="[active ? 'bg-gray-100 dark:bg-neutral-700' : '']"
                        >
                          <div class="flex gap-x-3">
                            <div>
                              <InboxIcon class="w-6 h-6 text-black dark:text-gray-200" />
                            </div>
                            <div>
                              <p class="text-left">
                                Nessuna notifica
                              </p>
                            </div>
                          </div>
                        </button>
                      </MenuItem>
                    </template>

                    <template v-else>
                      <MenuItem v-slot="{ active }">
                        <button
                          type="button" class="block w-full px-4 py-2 text-sm"
                          :class="[active ? 'bg-gray-100 dark:bg-neutral-700' : '']"
                          @click="() => markAllNotificationsAsRead()"
                        >
                          <p class="text-center">
                            Segna tutto come letto
                          </p>
                        </button>
                      </MenuItem>

                      <hr class="dark:border-neutral-500">

                      <MenuItem v-for="notification in notifications" :key="notification.id">
                        <button
                          type="button" class="group block w-full px-4 py-2 text-sm"
                          @click="() => performNotificationDefaultAction(notification)"
                        >
                          <div class="flex items-center justify-between">
                            <p class="text-left">
                              {{ notification.label }}
                              <span class="text-xs text-neutral-500">
                                <ClientOnly>
                                  {{ format(notification.createdAt, 'HH:mm - dd/MM/yy') }}
                                </ClientOnly>
                              </span>
                            </p>

                            <button
                              v-if="!notification.readAt" type="button"
                              class="p-2 transition rounded-full dark:group-hover:bg-neutral-700 group-hover:bg-gray-100"
                              @click="() => markNotificationAsRead(notification)"
                            >
                              <XMarkIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </button>
                      </MenuItem>
                    </template>
                  </MenuItems>
                </transition>
              </Menu>

              <!-- Profile dropdown -->
              <Menu as="div" class="relative">
                <div class="flex">
                  <MenuButton
                    class="flex flex-shrink-0 rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0095FF] focus:ring-offset-2"
                  >
                    <span class="sr-only">Open user menu</span>
                    <img class="flex-shrink-0 w-8 h-8 border rounded-full border-opacity-70 object-cover" :src="user.image">
                  </MenuButton>
                </div>
                <transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100"
                  leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100"
                  leave-to-class="transform scale-95 opacity-0"
                >
                  <MenuItems
                    class="absolute right-0 z-10 w-48 py-1 mt-2 text-gray-700 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg dark:divide-gray-700 dark:bg-neutral-800 dark:text-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                  >
                    <div class="py-1">
                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/lessons">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <VideoCameraIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                            <p class="transition group-hover:translate-x-1">
                              Videocorsi
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/audiobooks">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <MicrophoneIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                            <p class="transition group-hover:translate-x-1">
                              Audiolibri
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/watchlist">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <BookmarkIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                            <p class="transition group-hover:translate-x-1">
                              Preferiti
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/bookmarks">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <BookmarkSquareIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                            <p class="transition group-hover:translate-x-1">
                              Segnalibri
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/my-posts">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <NewspaperIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                            <p class="transition group-hover:translate-x-1">
                              I miei post
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/reminders">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <BellAlertIcon class="w-5 h-5 mr-2" />
                            <p class="transition group-hover:translate-x-1">
                              SRS
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/redeem-gift">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group text-default-blue hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <GiftIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                            <p class="transition group-hover:translate-x-1">
                              Riscatta un regalo
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ close }">
                        <NuxtLink v-slot="{ href, navigate }" custom to="/settings">
                          <a
                            class="flex items-center px-4 py-2 text-sm cursor-pointer group text-default-blue hover:bg-gray-100 hover:dark:bg-neutral-700"
                            v-bind="$attrs" :href="href" @click="(e) => { navigate(e), close() }"
                          >
                            <CogIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                            <p class="transition group-hover:translate-x-1">
                              Impostazioni
                            </p>
                          </a>
                        </NuxtLink>
                      </MenuItem>

                      <MenuItem v-slot="{ active }">
                        <button
                          type="button" class="flex items-center w-full px-4 py-2 text-sm text-left text-red-400 group"
                          :class="[active ? 'bg-gray-100 dark:bg-neutral-700' : '']" @click="signOut"
                        >
                          <ArrowUturnLeftIcon class="w-5 h-5 mr-2 rotate-180" aria-hidden="true" />
                          <p class="transition group-hover:translate-x-1">
                            Esci
                          </p>
                        </button>
                      </MenuItem>
                    </div>

                    <div v-if="canAccessAdminArea" class="py-1">
                      <MenuItem v-slot="{ active }">
                        <a
                          href="https://admin.scuolaperricchi.com/"
                          class="block px-4 py-2 text-sm font-bold text-center text-default-blue"
                          :class="[active ? 'bg-gray-100 dark:bg-neutral-700' : '']"
                        >
                          Area Admin
                        </a>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>

            <!-- Open mobile menu button -->
            <div class="flex items-center -mr-2 md:hidden w-full justify-end">
              <!-- <Menu v-if="!open" as="div" class="ml-3">
              <div class="flex">
                <MenuButton class="inline-flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-800">
                  <span class="sr-only">View notifications</span>
                  <BellIcon class="block w-6 h-6 m-2" aria-hidden="true" />
                </MenuButton>
              </div>
              <transition enter-active-class="transition duration-200 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
                <MenuItems class="absolute right-0 z-10 w-full py-1 mt-2 text-gray-700 origin-top bg-white divide-y rounded-md shadow-lg divide-neutral-700 top-20 dark:bg-neutral-800 dark:text-white">
                  <div class="absolute w-full h-screen bg-opacity-75 backdrop-blur-sm bg-neutral-900">
                    <template v-if="notifications.length === 0">
                      <MenuItem v-slot="{ active }">
                        <button type="button" class="block w-full px-4 py-2 text-sm" :class="[active ? 'bg-gray-100 dark:bg-neutral-700' : '']">
                          <div class="flex gap-x-3">
                            <div>
                              <InboxIcon class="w-6 h-6 text-black dark:text-gray-200" />
                            </div>
                            <div>
                              <p class="text-left">
                                Nessuna notifica
                              </p>
                            </div>
                          </div>
                        </button>
                      </MenuItem>
                    </template>

                    <template v-else>
                      <MenuItem
                        v-for="notification in notifications"
                        :key="notification.id"
                      >
                        <div class="block px-4 py-2 text-sm">
                          <div class="flex items-center gap-x-3">
                            <div v-if="!notification.readAt">
                              <div class="flex-shrink-0 w-2 h-2 rounded-full bg-default-blue" />
                            </div>
                            <div>
                              <p class="text-left">
                                {{ notification.label }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </MenuItem>
                    </template>
                  </div>
                </MenuItems>
              </transition>
            </Menu> -->

              <button v-if="open" type="button" class="inline-flex items-center justify-center p-2 rounded-md">
                <span class="sr-only">Dark or light mode</span>

                <ToggleDarkButton />
              </button>

              <DisclosureButton
                class="inline-flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-800"
              >
                <span class="sr-only">Open main menu</span>

                <div v-if="!open" class="flex">
                  <img class="flex-shrink-0 w-6 h-6 border rounded-full border-opacity-70 object-cover mr-2" :src="user.image" @click="openUserMenu">
                  <Bars3Icon class="block w-6 h-6" aria-hidden="true" @click="openNavigationMenu" />
                </div>
                <XMarkIcon v-else class="block w-6 h-6" aria-hidden="true" />
              </DisclosureButton>
            </div>
          </div>
        </div>
      </div>

      <DisclosurePanel class="md:hidden max-h-screen overflow-auto">
        <div v-if="isNavigationMenuOpen" class="pb-3 sm:space-y-1 pt-10">
          <template v-for="item in navigationItems" :key="item.name">
            <DisclosureButton
              v-if="item.external"
              as="a"
              v-bind="$attrs"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="flex p-1.5 text-sm justify-center w-full font-semibold leading-6 gap-x-3 hover:bg-gray-200 dark:hover:bg-neutral-800"
            >
              <div>
                {{ item.name }}
              </div>
            </DisclosureButton>
            <NuxtLink
              v-else
              v-slot="{ href, navigate, isExactActive }"
              :to="item.href" custom
            >
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex p-1.5 text-sm justify-center w-full font-semibold leading-6 gap-x-3 hover:bg-gray-200 dark:hover:bg-neutral-800"
                @click="navigate"
              >
                <div :class="[isExactActive ? 'border-b-2 border-default-blue' : '']">
                  {{ item.name }}
                </div>
              </DisclosureButton>
            </NuxtLink>
          </template>
        </div>
        <div v-if="isUserMenuOpen" class="pt-4 pb-3 border-t border-gray-200 dark:border-neutral-700">
          <div class="flex justify-between mt-7">
            <div class="flex items-center px-4">
              <div class="flex-shrink-0">
                <img class="w-10 h-10 rounded-full object-cover" :src="user.image" alt="">
              </div>
              <div class="ml-3">
                <div class="text-base font-medium text-gray-800 dark:text-gray-200">
                  {{ user.name }}
                </div>
                <div class="text-sm font-medium text-gray-500">
                  {{ user.email }}
                </div>
              </div>
            </div>
            <div>
              <button
                type="button"
                class="w-full flex px-2 py-1.5 text-red-400 text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                @click="signOut"
              >
                <ArrowUturnLeftIcon class="w-5 h-5 mr-2 rotate-180" aria-hidden="true" />
                Esci
              </button>
            </div>
          </div>

          <div class="mt-3 sm:space-y-1">
            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/lessons" custom>
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <VideoCameraIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                Videocorsi
              </DisclosureButton>
            </NuxtLink>

            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/audiobooks" custom>
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <MicrophoneIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                Audiolibri
              </DisclosureButton>
            </NuxtLink>

            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/watchlist" custom>
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <BookmarkIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                Preferiti
              </DisclosureButton>
            </NuxtLink>

            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/bookmarks" custom>
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <BookmarkSquareIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                Segnalibri
              </DisclosureButton>
            </NuxtLink>

            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/my-posts" custom>
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <NewspaperIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                I miei post
              </DisclosureButton>
            </NuxtLink>

            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/reminders" custom>
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <BellAlertIcon class="w-5 h-5 mr-2" />

                SRS
              </DisclosureButton>
            </NuxtLink>

            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/redeem-gift">
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-default-blue text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <GiftIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                Riscatta un regalo
              </DisclosureButton>
            </NuxtLink>

            <NuxtLink v-slot="{ href, navigate, isExactActive }" to="/settings">
              <DisclosureButton
                as="a" v-bind="$attrs" :href="href"
                class="flex px-2 py-1.5 text-default-blue text-sm font-semibold leading-6 hover:bg-gray-200 dark:hover:bg-neutral-800"
                :class="[isExactActive ? 'bg-gray-200 dark:bg-gray-800 border-l-4 border-default-blue' : '']"
                @click="navigate"
              >
                <CogIcon class="w-5 h-5 mr-2" aria-hidden="true" />
                Impostazioni
              </DisclosureButton>
            </NuxtLink>
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>

    <!-- Main -->
    <div class="flex flex-col flex-1">
      <slot />
    </div>

    <WhatsappFloatingButton
      ref="whatsappFloatingButton"
      :data="whatsappButtonData"
    />

    <!-- Offer Dialog -->
    <ClientOnly>
      <TransitionRoot as="template" :show="showCurrentPopup">
        <Dialog as="div" class="relative z-50" @close="closeCurrentPopup">
          <TransitionChild
            as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
            leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-neutral-900 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as="template" enter="ease-out duration-300"
                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                leave-from="opacity-100 translate-y-0 sm:scale-100"
                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6"
                >
                  <div class="absolute right-0 top-0 block pr-4 pt-4">
                    <button
                      type="button" class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      @click="() => closeCurrentPopup()"
                    >
                      <span class="sr-only">Close</span>
                      <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div class="sm:flex sm:items-start">
                    <div class="px-2 md:px-8 py-8 text-black">
                      <DialogTitle as="h3" class="text-center text-2xl pb-2 font-bold underline">
                        Solo fino al 22 ottobre!
                      </DialogTitle>

                      <h2 class="pb-4 uppercase text-5xl font-bold text-center text-black tracking-wide">
                        BLACK FRIDAY BIGLUCHIANO!
                      </h2>

                      <!-- <img src="~/assets/img/popup/image1.png" class="max-w-[400px] mb-5 rounded-md mx-auto"> -->

                      <p class="text-center text-xl font-semibold">
                        Follia. <u>Solo fino al 30 novembre</u>!
                      </p>

                      <p class="text-center">
                        Signori, solo per gli studenti del Papi è disponibile il Black Friday Bigluchiano.
                        <br><br>
                        In pratica, ci sono <strong>prezzi folli e super sconti un po’ su tutto il nostro listino</strong>.
                        <br><br>
                        Infatti, <u>solo fino al 30 novembre</u>, i miei venditori saranno di manica moooolto larga e vi faranno avere ciò che vi serve a prezzi scandalosi.
                        <br><br>
                        Che tu voglia un corso in particolare, alcuni corsi in particolare, tutti i nostri corsi, le nostre masterclass,
                        consulenze con i coach o quant’altro, fatti un regalo e scrivi ora al tuo VTA o
                        <a class="font-bold underline" target="_blank" href="https://wa.me/18573021300">su Whatsapp al +1 (857) 302-1300 (prefisso americano)</a> per un’offerta dedicata.
                        <br><br>
                        Così, per una volta, non comprerai solo cagate al Black Friday, ma qualcosa che
                        <strong>potrà impattare - col giusto impegno - sul tuo patrimonio futuro</strong>.
                        <br><br>
                        Buon Black Friday e buon lucro.
                        <br><br>
                        Cervellino su “On” signori.
                        <br><br><br>
                        Big Luca,
                        <br>
                        Presidente del Big Luca Group
                      </p>

                      <div class="mt-6 text-black">
                        <div class="mt-12 py-2">
                          <img class="h-[55px]" src="~/assets/img/big-luca-signature.png">
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
    </ClientOnly>

    <!-- Cleopatra Plan Dialog -->
    <ClientOnly>
      <TransitionRoot as="template" :show="showCleopatraPopup">
        <Dialog as="div" class="relative z-50" @close="closeCleopatraPopup">
          <TransitionChild
            as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
            leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-neutral-900 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <TransitionChild
                as="template" enter="ease-out duration-300"
                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                leave-from="opacity-100 translate-y-0 sm:scale-100"
                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6"
                >
                  <div class="absolute right-0 top-0 block pr-4 pt-4">
                    <button
                      type="button" class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      @click="() => closeCleopatraPopup()"
                    >
                      <span class="sr-only">Close</span>
                      <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div class="sm:flex sm:items-start">
                    <div class="px-2 md:px-6 py-6 text-black w-full">
                      <DialogTitle as="h3" class="text-center text-3xl pb-4 font-bold">
                        Benvenuto in Cleopatra!
                      </DialogTitle>

                      <p class="text-center text-lg">
                        Hai un piano attivo su Cleopatra. Visita il sito di Cleopatra per accedere ai tuoi contenuti e sfruttare al massimo il tuo piano.
                      </p>

                      <div class="mt-8 flex justify-center">
                        <a
                          href="https://app.cleopatraplatform.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="inline-flex items-center justify-center rounded-md bg-default-blue px-6 py-3 text-base font-semibold text-white shadow-sm hover:opacity-90 focus:outline-none"
                          @click="() => closeCleopatraPopup()"
                        >
                          Vai al sito di Cleopatra
                        </a>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
    </ClientOnly>
  </div>
</template>
